import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";

// Mirrors the cap in the watchlist_cap trigger (migration 066). The trigger is
// what actually holds; this copy exists so the button can be disabled with an
// explanation rather than the user finding out from a failed insert.
export const WATCHLIST_LIMIT = 15;

// The watchlist is read in two places at once: the star button inside
// TeamDrawer and the filter chip on the Teams tab, which can both be on screen
// during the same interaction. A per-component fetch would let them disagree
// for as long as the other one took to refetch, so the list is held once per
// member here and every hook instance subscribes to it.
//
// Not a React context: the Teams tab and the drawer sit under different
// providers (the drawer is also opened from the draft room and the scoreboard),
// and a fourth provider wrapped around all of them to share one small Set is
// more plumbing than the problem deserves.
const _byMember = new Map(); // member id -> Set of team slugs
const _listeners = new Set();
const _inFlight = new Map(); // member id -> Promise, so N hooks cause 1 fetch

function emit() {
  _listeners.forEach((fn) => fn());
}

function snapshot(memberId) {
  return (memberId && _byMember.get(memberId)) || null;
}

async function load(memberId) {
  if (_inFlight.has(memberId)) return _inFlight.get(memberId);

  const promise = (async () => {
    const { data, error } = await supabase
      .from("watchlist_entries")
      .select("team_id")
      .eq("member_id", memberId);

    if (error) {
      console.error("Watchlist fetch error:", error);
      // An empty set rather than leaving it null: null means "not loaded yet"
      // and keeps every star button in its loading state forever.
      _byMember.set(memberId, new Set());
    } else {
      _byMember.set(memberId, new Set((data || []).map((r) => r.team_id)));
    }

    _inFlight.delete(memberId);
    emit();
  })();

  _inFlight.set(memberId, promise);
  return promise;
}

/**
 * The current member's watchlist, as a Set of team slugs, plus a toggle.
 *
 * `slugs` is null until the first load finishes. `toggle` returns a string
 * when it refused (the reason, ready to show the user) and null on success.
 */
export function useWatchlist(memberId, leagueId) {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    _listeners.add(listener);
    return () => { _listeners.delete(listener); };
  }, []);

  useEffect(() => {
    if (!memberId || _byMember.has(memberId)) return;
    load(memberId);
  }, [memberId]);

  const slugs = snapshot(memberId);

  // league_id is denormalized onto the row rather than reached through
  // member_id. It is what makes the cascade on leagues delete the entries, and
  // it saves every read joining league_members to answer "which league is this".
  const toggle = useCallback(async (slug) => {
    if (!memberId || !leagueId || !slug) return "You need to be a member of this league.";

    const current = _byMember.get(memberId);
    if (!current) return "Still loading your watchlist.";

    const watched = current.has(slug);

    if (!watched && current.size >= WATCHLIST_LIMIT) {
      return `Your watchlist is full. Remove a team to add another. The limit is ${WATCHLIST_LIMIT}.`;
    }

    // Optimistic: the star flips immediately and reverts if the write fails.
    // A round trip on every tap made the button feel broken on a slow
    // connection, and the operation is a single row either way.
    const next = new Set(current);
    if (watched) next.delete(slug); else next.add(slug);
    _byMember.set(memberId, next);
    emit();

    const { error } = watched
      ? await supabase
          .from("watchlist_entries")
          .delete()
          .eq("member_id", memberId)
          .eq("team_id", slug)
      : await supabase
          .from("watchlist_entries")
          .insert({ member_id: memberId, team_id: slug, league_id: leagueId });

    if (error) {
      _byMember.set(memberId, current);
      emit();
      console.error("Watchlist write error:", error);
      // check_violation is what the cap trigger raises. Anything else is a
      // genuine failure and gets the generic line.
      if (error.code === "23514") {
        return `Your watchlist is full. Remove a team to add another. The limit is ${WATCHLIST_LIMIT}.`;
      }
      return "That did not save. Try again.";
    }

    return null;
  }, [memberId, leagueId]);

  return {
    slugs,
    count: slugs ? slugs.size : 0,
    limit: WATCHLIST_LIMIT,
    full: Boolean(slugs && slugs.size >= WATCHLIST_LIMIT),
    isWatched: useCallback((slug) => Boolean(slugs && slug && slugs.has(slug)), [slugs]),
    toggle,
  };
}
